import { RefObject, useCallback, useRef } from 'react';
import type { Canvas } from 'fabric';
import type { FieldBinding } from '@doer/frontend-components/creativity-editor';
import { applyAllBindings, type ApplyToModel } from '../binding';
import type { RoutedFormData } from '../../../types/templates';

type ComputeDefaults = (data: RoutedFormData, resolutionId?: string) => RoutedFormData;
type Pending = { data: RoutedFormData; resolutionId: string };

type UseBindingPipelineParams = {
  canvas: Canvas | null;
  activeResolutionId: string;
  fieldBindings: FieldBinding[];
  computeDefaults: ComputeDefaults;
  onAfterBinding: (data: RoutedFormData) => void;
  applyToModel?: ApplyToModel;
};

type BindingPipelineApi = {
  enqueue: (data: RoutedFormData) => void;
  flush: () => Promise<void>;
  isApplyingRef: RefObject<boolean>;
};

/**
 * Serialized canvas binding queue with last-write-wins coalescing.
 * Rapid edits overwrite the pending slot before the runner consumes it;
 * effects are idempotent so re-running with the latest data is cheap.
 * Prevents out-of-order setSrc completions from leaving the canvas stale.
 */
export const useBindingPipeline = ({
  canvas,
  activeResolutionId,
  fieldBindings,
  computeDefaults,
  onAfterBinding,
  applyToModel
}: UseBindingPipelineParams): BindingPipelineApi => {
  const pendingRef = useRef<Pending | null>(null);
  const runningRef = useRef(false);
  const lastRunRef = useRef<Promise<void>>(Promise.resolve());
  const isApplyingRef = useRef(false);

  // Mirrors active resolution so runner sees the latest without invalidating its closure.
  const activeIdRef = useRef(activeResolutionId);
  activeIdRef.current = activeResolutionId;

  const run = useCallback((): Promise<void> => {
    if (runningRef.current) return lastRunRef.current;
    runningRef.current = true;
    lastRunRef.current = (async () => {
      try {
        while (pendingRef.current) {
          const { data, resolutionId } = pendingRef.current;
          pendingRef.current = null;
          if (resolutionId !== activeIdRef.current || !canvas) continue;

          const defaults = computeDefaults(data, resolutionId);
          isApplyingRef.current = true;
          try {
            await applyAllBindings(canvas, data, fieldBindings, resolutionId, defaults, applyToModel);
          } finally {
            isApplyingRef.current = false;
          }

          // Only propagate if the user hasn't switched resolution mid-flight.
          if (resolutionId === activeIdRef.current) {
            onAfterBinding(data);
          }
        }
      } finally {
        runningRef.current = false;
      }
    })();
    return lastRunRef.current;
  }, [canvas, fieldBindings, computeDefaults, onAfterBinding, applyToModel]);

  const flush = useCallback((): Promise<void> => lastRunRef.current, []);

  const enqueue = useCallback(
    (data: RoutedFormData) => {
      pendingRef.current = { data, resolutionId: activeIdRef.current };
      void run();
    },
    [run]
  );

  return { enqueue, flush, isApplyingRef };
};
